import { APP_CODES, type AppCode } from "@luckymenu/core";
import { TRPCClientError } from "@trpc/client";

/** An app code, or NETWORK when the API could not be reached. */
export type ClientErrorCode = AppCode | "NETWORK";

const KNOWN = new Set<string>(APP_CODES);

/** Reads the stable `appCode` from any error thrown by an API call. */
export function errorCodeOf(error: unknown): ClientErrorCode {
	if (error instanceof TRPCClientError) {
		const code = (error.data as { appCode?: unknown } | undefined)?.appCode;
		if (typeof code === "string" && KNOWN.has(code)) return code as AppCode;
		// No tRPC error body: the request never got a proper answer.
		if (!error.data) return "NETWORK";
		return "INTERNAL";
	}
	if (error instanceof TypeError) return "NETWORK";
	return "INTERNAL";
}

/** Only failures that may succeed later are retried. */
export function isRetryable(error: unknown): boolean {
	const code = errorCodeOf(error);
	return code === "NETWORK" || code === "INTERNAL" || code === "RATE_LIMITED";
}

/** The safe `details` an API error carries, such as `{ reason: "taken" }`. Empty if none. */
export function errorDetailsOf(error: unknown): Record<string, unknown> {
	if (error instanceof TRPCClientError) {
		const details = (error.data as { details?: unknown } | undefined)?.details;
		if (details && typeof details === "object") return details as Record<string, unknown>;
	}
	return {};
}

/** Field problems from a `VALIDATION` error, keyed by the first path segment. */
export function errorFieldsOf(error: unknown): Record<string, string> {
	if (!(error instanceof TRPCClientError)) return {};
	const data = error.data as
		| { issues?: { path: string[]; message: string }[]; details?: { field?: unknown } }
		| undefined;
	const fields: Record<string, string> = {};
	for (const issue of data?.issues ?? []) {
		const key = issue.path[0];
		if (key && !(key in fields)) fields[key] = issue.message;
	}
	const field = data?.details?.field;
	if (typeof field === "string" && !(field in fields)) fields[field] = error.message;
	return fields;
}
